// src/components/ServiceRequestForm.js

import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";
import "../../styles/ServiceRequestForm.css";

const ServiceRequestForm = () => {
  const [name, setName] = useState("");
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("");
  const [serviceType, setServiceType] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [preferredContact, setPreferredContact] = useState("Email");
  const [preferredDays, setPreferredDays] = useState([]);
  const [preferredTime, setPreferredTime] = useState("");
  const [showDaysDropdown, setShowDaysDropdown] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const daysDropdownRef = useRef(null);
  const timeDropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const type = query.get("serviceType");
    if (type) {
      setServiceType(type);
    }
  }, [location]);

  const handleDayChange = (day) => {
    setPreferredDays((prevDays) =>
      prevDays.includes(day)
        ? prevDays.filter((d) => d !== day)
        : [...prevDays, day]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const address = `${address1}, ${address2}, ${city}, ${state}, ${zip}`;
    const formData = {
      name,
      address,
      phone,
      email,
      description,
      serviceType,
      additionalDetails,
      preferredContact,
      preferredDays,
      preferredTime,
    };

    try {
      await axiosInstance.post("/requests", formData);
      alert(
        "Request sent, we will contact you about your inquiry in less than two business days."
      );
      navigate("/"); // Redirect to the home page
    } catch (err) {
      console.error(err);
      alert("There was an error submitting your request. Please try again.");
    }
  };

  const toggleDaysDropdown = () => {
    setShowDaysDropdown(!showDaysDropdown);
  };

  const toggleTimeDropdown = () => {
    setShowTimeDropdown(!showTimeDropdown);
  };

  const handleClickOutside = (event) => {
    if (
      daysDropdownRef.current &&
      !daysDropdownRef.current.contains(event.target)
    ) {
      setShowDaysDropdown(false);
    }
    if (
      timeDropdownRef.current &&
      !timeDropdownRef.current.contains(event.target)
    ) {
      setShowTimeDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="service-request-form">
      <div className="form-header">
        <h2>Request Service</h2>
        <p>Tell us what you need, and we'll get back to you within 2 business days.</p>
      </div>
      <form onSubmit={handleSubmit}>
        {/* Section 1: Service Details */}
        <div className="form-section">
          <h3>🛠️ Service Details</h3>
          <div className="form-group">
            <label>Type of Service</label>
            <select
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value)}
              required
            >
              <option value="" disabled>Select a service</option>
              <option value="Heating">Heating</option>
              <option value="Ventilation">Ventilation</option>
              <option value="Air Conditioning">Air Conditioning</option>
              <option value="Other">Other</option>
              <option value="Multiple">Multiple</option>
            </select>
          </div>
          {(serviceType === "Other" || serviceType === "Multiple") && (
            <div className="form-group">
              <label>Specify Service</label>
              <textarea
                value={additionalDetails}
                onChange={(e) => setAdditionalDetails(e.target.value)}
                className="additional-details-textarea"
                required
              ></textarea>
            </div>
          )}
          <div className="form-group">
            <label>Description</label>
            <textarea
              placeholder="Please describe the issue or service needed..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            ></textarea>
          </div>
        </div>

        {/* Section 2: Service Location */}
        <div className="form-section">
          <h3>📍 Service Location</h3>
          <div className="form-group">
            <label>Address Line 1</label>
            <input
              type="text"
              value={address1}
              onChange={(e) => setAddress1(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Address Line 2 (Optional)</label>
            <input
              type="text"
              value={address2}
              onChange={(e) => setAddress2(e.target.value)}
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>State</label>
              <input
                type="text"
                value={state}
                onChange={(e) => setState(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Zip Code</label>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Section 3: Contact Information */}
        <div className="form-section">
          <h3>👤 Contact Information</h3>
          <div className="form-group">
            <label>Full Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Phone Number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label>Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
          </div>
        </div>

        {/* Section 4: Preferences */}
        <div className="form-section">
          <h3>📅 Preferences</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Preferred Contact Method</label>
              <select
                value={preferredContact}
                onChange={(e) => setPreferredContact(e.target.value)}
                required
              >
                <option value="Email">Email</option>
                <option value="Phone">Phone</option>
              </select>
            </div>
            <div className="form-group">
              <label>Preferred Days</label>
              <div className="dropdown" ref={daysDropdownRef}>
                <button
                  type="button"
                  className="dropdown-toggle"
                  onClick={toggleDaysDropdown}
                >
                  {preferredDays.length > 0
                    ? preferredDays.map((day) => day.substring(0, 3)).join(", ")
                    : "Select Days"}
                </button>
                {showDaysDropdown && (
                  <div className="dropdown-menu">
                    {[
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                      "Sunday",
                    ].map((day) => (
                      <label key={day} className="checkbox-label">
                        <input
                          type="checkbox"
                          value={day}
                          checked={preferredDays.includes(day)}
                          onChange={() => handleDayChange(day)}
                        />
                        {day}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Preferred Time</label>
              <div className="dropdown" ref={timeDropdownRef}>
                <button
                  type="button"
                  className="dropdown-toggle"
                  onClick={toggleTimeDropdown}
                >
                  {preferredTime ? preferredTime : "Select Time"}
                </button>
                {showTimeDropdown && (
                  <div className="dropdown-menu">
                    {["Morning", "Afternoon"].map((time) => (
                      <label key={time} className="radio-label">
                        <input
                          type="radio"
                          value={time}
                          checked={preferredTime === time}
                          onChange={(e) => setPreferredTime(e.target.value)}
                        />
                        {time}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <button type="submit" className="submit-btn">Submit Request</button>
      </form>
    </div>
  );
};

export default ServiceRequestForm;
